import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import BuddhistSchoolBanner from "@/components/BuddhistSchoolBanner";
import { useEffect, useState } from "react";
import { getCurrentUser } from "@/services/authService";
import FeaturedItems from "@/components/FeaturedItems";
import { Trophy, BookOpen, MessageSquare, Gamepad2, LogIn, SmartphoneIcon } from "lucide-react";
import { SEO } from "@/components/ui/seo";
import { SearchBox } from "@/components/ui/search-box";

// Import các component mới
import QuickChatWidget from "@/components/QuickChatWidget";
import LearningJourneyWidget from "@/components/LearningJourneyWidget";
import GameCardGrid from "@/components/GameCardGrid";

// Hero Section Component - Đã cập nhật với QuickChatWidget và SearchBox
const HeroSection = () => {
  const { t } = useTranslation('home');
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const user = await getCurrentUser();
        setIsLoggedIn(!!user);
      } catch (error) {
        console.error('Error checking login status:', error);
        setIsLoggedIn(false);
      }
    };

    checkLoginStatus();
  }, []);

  return (
    <section className="bg-gradient-to-r from-primary/10 to-primary/30 dark:from-primary/20 dark:to-primary/40 py-12 md:py-20">
      <div className="container px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 order-1">
            <div className="flex flex-col items-start space-y-4">
              <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
                Giác Ngộ
                </h1>
                <p className="max-w-[700px] text-gray-500 md:text-xl dark:text-gray-400">
                Ứng dụng học Giác Ngộ qua sơ đồ tương tác
                </p>
              </div>
              
              {/* Search Box - Thêm mới */}
              <div className="relative w-full max-w-2xl mt-2">
                <SearchBox className="w-full" variant="default" />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-2">
                <Button
                  asChild
                  className="bg-gradient-to-r from-amber-500 to-amber-300 hover:from-amber-600 hover:to-amber-400 border-none"
                >
                  <Link to="/courses">
                  Khám phá khóa học
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/diagrams">
                    Khám phá sơ đồ
                  </Link>
                </Button>
              </div>

              {/* Widget Tra cứu Kinh điển */}
              <div className="mt-6 w-full max-w-md p-4 rounded-lg bg-gradient-to-r from-amber-50/90 to-orange-50/90 dark:from-amber-950/60 dark:to-orange-950/60 border border-amber-200 dark:border-amber-800 shadow-md relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  {/* Pattern với các biểu tượng Phật giáo */}
                  <div className="absolute top-1/4 left-1/4 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12">
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-full h-full text-amber-700"
                    >
                      <path d="M12,2L4.5,20.29L5.21,21L12,18L18.79,21L19.5,20.29L12,2Z" />
                    </svg>
                  </div>
                  <div className="absolute top-3/4 right-1/4 transform translate-x-1/2 -translate-y-1/2 w-10 h-10">
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-full h-full text-amber-700"
                    >
                      <path d="M12,8L15.5,13.5L13.5,13.5L13.5,20H10.5V13.5H8.5L12,8M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2Z" />
                    </svg>
                  </div>
                  <div className="absolute bottom-1/4 left-3/4 transform -translate-x-1/2 translate-y-1/2 w-14 h-14">
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-full h-full text-amber-700"
                    >
                      <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,10.5A1.5,1.5 0 0,0 10.5,12A1.5,1.5 0 0,0 12,13.5A1.5,1.5 0 0,0 13.5,12A1.5,1.5 0 0,0 12,10.5Z" />
                    </svg>
                  </div>
                </div>

                <div className="relative z-10">
                  <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-200 flex items-center">
                    <BookOpen className="h-5 w-5 mr-2 text-amber-600 dark:text-amber-400" />
                    Tra cứu kinh điển
                  </h3>
                  <p className="mt-2 text-sm text-amber-800 dark:text-amber-300">
                    Tìm kiếm trích dẫn, nguồn gốc và ý nghĩa của kinh điển Phật
                    giáo
                  </p>
                  <div className="mt-3">
                    <Button
                      asChild
                      variant="secondary"
                      className="w-full bg-amber-100 hover:bg-amber-200 text-amber-900 dark:bg-amber-900/60 dark:hover:bg-amber-900/80 dark:text-amber-100 border border-amber-300 dark:border-amber-700"
                    >
                      <Link
                        to="/sutra-search"
                        className="flex items-center justify-center"
                      >
                        <BookOpen className="h-4 w-4 mr-2" />
                        Tra cứu ngay
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1 flex items-center justify-center order-2">
            <div className="w-full relative max-w-md mx-auto md:mr-0">
              {isLoggedIn ? (
                <QuickChatWidget className="shadow-xl transform hover:scale-105 transition-transform duration-300" />
              ) : (
                <div className="bg-white rounded-3xl shadow-lg p-6 relative shadow-xl transform hover:scale-105 transition-transform duration-300">
                  <div className="flex items-start mb-5">
                    <div className="flex-shrink-0 w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                      <MessageSquare className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-3 bg-amber-50 px-5 py-3 rounded-2xl rounded-tl-none max-w-[85%]">
                      <p className="text-amber-900">Xin chào! Đăng nhập để trò chuyện với trí tuệ Phật giáo!</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start justify-end mb-5">
                    <div className="mr-3 bg-blue-50 px-5 py-3 rounded-2xl rounded-tr-none max-w-[85%]">
                      <p className="text-blue-900">
                        Tôi muốn tìm hiểu thêm về Thiền...
                      </p>
                    </div>
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-800 text-lg font-medium">👤</span>
                    </div>
                  </div>

                  <Button
                    asChild
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white border-none h-14 rounded-xl"
                  >
                    <Link to="/login" className="flex items-center justify-center">
                      <LogIn className="mr-2 h-5 w-5" />
                      Đăng nhập để chat
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main Home Page Component
const Home = () => {
  const { t } = useTranslation('home');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const user = await getCurrentUser();
        setIsLoggedIn(!!user);
      } catch (error) {
        console.error('Error checking login status:', error);
        setIsLoggedIn(false);
      }
    };

    checkLoginStatus();
  }, []);

  return (
    <div className="min-h-screen">
      <SEO 
        title="Giác Ngộ - Trường học Phật giáo online"
        description="Khám phá Giác Ngộ - Nền tảng học tập Phật giáo trực tuyến với sơ đồ trực quan và công cụ học tập tương tác."
        isHome={true}
      />

      <HeroSection />

      {/* Banner Section */}
      <section className="py-12 bg-background">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <BuddhistSchoolBanner />
        </div>
      </section>

      {/* Khóa học nổi bật */}
      <section className="py-12 bg-background">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FeaturedItems type="courses" maxItems={4} />
        </div>
      </section>

      {/* Sơ đồ nổi bật với layout giống PopularDiagramsSection */}
      <section className="py-12 bg-muted/50">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FeaturedItems type="diagrams" maxItems={3} />
        </div>
      </section>

      {/* Phần Your Personal Dharma Journey - Hành trình học tập & Game Quiz cùng hiển thị */}
      <section className="py-12 bg-muted/50">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {isLoggedIn ? (
              <LearningJourneyWidget className="h-full" />
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden h-full">
                <div className="p-6 flex flex-col h-full">
                  <h3 className="text-xl font-bold mb-4">Hành trình học tập của bạn</h3>
                  <p className="mb-6 text-muted-foreground">
                    Đăng nhập để theo dõi tiến độ học tập, nhận chứng chỉ và truy cập các nội dung độc quyền dành cho thành viên.
                  </p>
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 mb-6">
                    <h4 className="font-medium text-amber-800 dark:text-amber-300 mb-2">Lợi ích khi trở thành thành viên:</h4>
                    <ul className="list-disc pl-5 text-amber-700 dark:text-amber-400 space-y-1 text-sm">
                      <li>Lưu tiến độ học tập</li>
                      <li>Nhận chứng chỉ hoàn thành khóa học</li>
                      <li>Tham gia đối thoại với AI Phật giáo</li>
                      <li>Theo dõi thành tích cá nhân</li>
                      <li>Truy cập nội dung nâng cao</li>
                    </ul>
                  </div>
                  <div className="mt-auto">
                    <Button
                      asChild
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                    >
                      <Link to="/login" className="flex items-center justify-center">
                        <LogIn className="mr-2 h-5 w-5" />
                        Đăng nhập ngay
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                <div className="p-5">
                  <h3 className="text-lg font-bold mb-3">Hành Trình Tâm</h3>
                  <p className="mb-4 text-muted-foreground text-sm">
                    Khám phá cách tâm phản ứng khi mắt tiếp xúc với vẻ đẹp
                  </p>
                  <Button asChild size="sm">
                    <Link to="/minigames/mind-matter?gameId=flower-journey">
                      <Gamepad2 className="mr-2 h-3 w-3" /> Chơi ngay
                    </Link>
                  </Button>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                <div className="p-5">
                  <h3 className="text-lg font-bold mb-3">Vượt Qua Hôn Trầm</h3>
                  <p className="mb-4 text-muted-foreground text-sm">
                    Trải nghiệm cách đối phó với hôn trầm thụy miên
                  </p>
                  <Button asChild size="sm">
                    <Link to="/minigames/mind-matter?gameId=sloth-torpor-journey">
                      <Gamepad2 className="mr-2 h-3 w-3" /> Thử thách
                    </Link>
                  </Button>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                <div className="p-5">
                  <h3 className="text-lg font-bold mb-3">Khi Lưỡi Nếm Vị</h3>
                  <p className="mb-4 text-muted-foreground text-sm">
                    Khám phá phản ứng tâm khi lưỡi tiếp xúc với các vị
                  </p>
                  <Button asChild size="sm">
                    <Link to="/minigames/mind-matter?gameId=taste-journey">
                      <Gamepad2 className="mr-2 h-3 w-3" /> Trải nghiệm
                    </Link>
                  </Button>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                <div className="p-5">
                  <h3 className="text-lg font-bold mb-3">Khi Ý Gặp Pháp</h3>
                  <p className="mb-4 text-muted-foreground text-sm">
                    Khám phá tâm phản ứng khi gặp suy nghĩ khó khăn
                  </p>
                  <Button asChild size="sm">
                    <Link to="/minigames/mind-matter?gameId=thought-journey">
                      <Gamepad2 className="mr-2 h-3 w-3" /> Khám phá
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gamification Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <GameCardGrid />
        </div>
      </section>

    </div>
  );
};

export default Home;
